/* eslint-disable no-param-reassign */
import mapKeys from 'lodash/mapKeys';
// @ts-expect-error
import { v3 as murmur } from 'murmur-hash';
import { nanoid } from 'nanoid';

import { BuiltInComponents } from 'craft/constants';
import { Action, CraftNode } from 'craft/types';

// workaround for circular dependency
export const CONDITIONAL_ID = '5';

export const hash = (...s: string[]): number => murmur.x86.hash128(s.join(''));

export const normalizeClonedNode = (
  id: string,
  salt = '',
  { updateFlowStepActions = false }: { updateFlowStepActions?: boolean },
) => (node: CraftNode) => {
  node.id = id;
  /**
   * Flow / Flow components
   * recreate new stateId in a bijective way, so that all component referencing the initial stateId will be
   * referenced the new stateId without altering the bindings
   */
  if (node.data.props.stateId) {
    node.data = {
      ...node.data,
      props: {
        ...node.data.props,
        stateId: hash(node.data.props.stateId, salt),
      },
    };
  }

  /**
   * Hotspots
   */
  if (node.data.name === BuiltInComponents.Hotspot) {
    node.data = {
      ...node.data,
      props: {
        ...node.data.props,
        stateId: nanoid(6),
        placementId: undefined,
      },
    };
  }

  /**
   * Tours
   */
  if (node.data.name === BuiltInComponents.TourStep) {
    node.data = {
      ...node.data,
      props: {
        ...node.data.props,
        placementId: undefined,
      },
    };
  }

  /**
   * Form / Form components
   * recreate new formId in a bijective way, so that all component referencing the initial formId will be
   * referenced the new formId without altering the bindings
   */
  if (node.data.name === BuiltInComponents.Form && node.data.props.formId) {
    node.data = {
      ...node.data,
      props: {
        ...node.data.props,
        formId: hash(node.data.props.formId, salt),
      },
    };
  }

  if (node.data.props.formInputId) {
    node.data = {
      ...node.data,
      props: {
        ...node.data.props,
        formInputId: hash(node.data.props.formInputId, salt),
      },
    };
  }

  if (node.data?.custom?.conditionals?.[CONDITIONAL_ID]?.if) {
    node.data = {
      ...node.data,
      custom: {
        ...node.data.custom,
        conditionals: {
          ...node.data.custom.conditionals,
          [CONDITIONAL_ID]: {
            ...node.data.custom.conditionals[CONDITIONAL_ID],
            if: {
              ...node.data.custom.conditionals[CONDITIONAL_ID].if,
              id: `ds-${nanoid(6)}`,
            },
          },
        },
      },
    };
  }

  const actionIndex = node.data.custom.actions?.findIndex((action: any) => action.handler?.stepId);
  // Only update the stepId on actions if the new tree has a Flow
  if (actionIndex > -1 && updateFlowStepActions) {
    node.data = {
      ...node.data,
      custom: {
        ...node.data.custom,
        actions: node.data.custom.actions.map((action: Action, index: number) =>
          index !== actionIndex
            ? action
            : {
                ...action,
                handler: {
                  ...action.handler,
                  stepId: hash(node.data.custom.actions[actionIndex].handler.stepId, salt),
                },
              },
        ),
      },
    };
  }

  if (node.data.props.stepId) {
    node.data = {
      ...node.data,
      props: {
        ...node.data.props,
        stepId: hash(node.data.props.stepId, salt),
      },
    };
  }

  /**
   * Component with states
   * recreate new state props in a bijective way, so that all component referencing the initial state will be
   * referenced the new state without altering the bindings
   */
  if (node.data.custom?.states) {
    // Regenerate state ids to preserve its uniqueness
    node.data = {
      ...node.data,
      custom: {
        ...node.data.custom,
        states: mapKeys(node.data.custom.states, (_, key) => hash(key, salt)),
      },
    };
  }

  /**
   * Checklist / checklist components
   * recreate new groupId in a bijective way, so that all component referencing the initial groupId will be
   * referenced the new groupId without altering the bindings
   */
  if (node.data.custom?.custom?.groupId) {
    // Regenerate group id
    node.data = {
      ...node.data,
      custom: {
        ...node.data.custom,
        custom: {
          ...node.data.custom.custom,
          groupId: hash(node.data.custom.custom.groupId, salt),
        },
      },
    };
  }
};
